import React, { FC, useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import { animated } from 'react-spring'
import styled from 'styled-components'

import colors from '../colors'
import { EXCHAGES_INTRO_URL, EXCHANGE_META } from '../consts'
import images from '../images'
import { useFadeIn } from '../utils/hooks'

const LoadingContainer = styled(animated.div)`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`
const Loading = styled.img.attrs({ src: images.loading })``
const Container = styled(animated.div)`
  display: flex;
  margin: 0 16px;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
`
const Title = styled.div`
  padding: 16px 0;
  font-size: 16px;
  color: white;
`
const RobotImg = styled.img.attrs({ src: images.robot })`
  width: 100%;
  height: auto;
`
const Content = styled.div`
  margin-bottom: 16px;
  padding: 16px 32px;
  align-self: stretch;

  display: flex;
  flex-direction: column;
  align-items: center;

  background-color: white;
  border-radius: 8px;
`
const Logo = styled.img`
  width: 220px;
  height: auto;
`
const Desc = styled.div`
  margin: 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: ${colors.black};
`
const FeatureList = styled.ul`
  padding: 0;
  align-self: flex-start;
  list-style-type: none;
`
const Feature = styled.li`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${colors.black};

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 2px;
    background-color: ${colors.blue};
  }
`

const Btn = styled.a`
  height: 48px;
  margin: 8px 0;
  align-self: stretch;

  display: flex;
  align-items: center;
  justify-content: center;

  text-decoration: none;
  border-radius: 24px;
  color: white;
  background-color: ${colors.blue};
  box-shadow: 0px 4px 0px 0px ${colors.darkBlue};
  cursor: pointer;
  user-select: none;
`
const BtnText = styled.span`
  font-weight: 500;
  font-size: 16px;
`

const DetailBtn = styled(Btn)`
  border: 1px solid ${colors.gray};
  color: ${colors.blue};
  background-color: white;
  box-shadow: 0px 4px 0px 0px ${colors.darkYellow};
`
const OthersBtn = styled(Btn)`
  border: 1px solid ${colors.gray};
  color: ${colors.blue};
  background-color: transparent;
  box-shadow: 0px 4px 0px 0px ${colors.darkYellow};
`

const twitterShare = (text: string, url: string, hashtags: string[]) => {
  const _hashtags = hashtags.map(a => encodeURI(a)).join(',')
  const a = `http://twitter.com/share?text=${encodeURI(text)}&url=${encodeURI(
    url,
  )}&hashtags=${_hashtags}`
  window.open(a, '_blank')
}

interface Props {
  code: string
  text: string
  onPressBackToIntro: () => void
}

const Body: FC<Props> = ({ code, text, onPressBackToIntro }) => {
  const meta = EXCHANGE_META[code]
  const anim = useFadeIn()
  return (
    <Container style={anim}>
      <Title>診断結果</Title>
      <RobotImg />
      <Content>
        <Logo src={meta.logo} />
        <Desc>{text}</Desc>
        <FeatureList>
          {meta.features.map(a => (
            <Feature key={a}>{a}</Feature>
          ))}
        </FeatureList>
        {meta.url && (
          <Btn
            onClick={() =>
              ReactGA.ga('send', {
                hitType: 'event',
                eventCategory: 'Exchange Diagnose',
                eventAction: 'Goto Exchange',
                eventLabel: code,
              })
            }
            href={meta.url}
            target="_blank"
          >
            <BtnText>今すぐ口座開設</BtnText>
          </Btn>
        )}
      </Content>
      <DetailBtn href={meta.introURL} target="_blank">
        <BtnText>詳細を見る</BtnText>
      </DetailBtn>
      <OthersBtn href={EXCHAGES_INTRO_URL} target="_blank">
        <BtnText>手数料ランキング</BtnText>
      </OthersBtn>
      <OthersBtn
        onClick={() =>
          twitterShare(
            '「ググっても、アフィ報酬高い順に並べた記事ばっかり」という方に！用途別の最適解がわかる簡単診断ツール',
            `https://coinjinja.com/torihikijo-shidan`,
            ['取引所診断'],
          )
        }
      >
        <BtnText>Twitterで共有</BtnText>
      </OthersBtn>
      <OthersBtn onClick={onPressBackToIntro}>
        <BtnText>最初に戻る</BtnText>
      </OthersBtn>
    </Container>
  )
}

const Result: FC<Props> = props => {
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    const handle = setTimeout(() => setLoading(false), 2000)
    return () => clearTimeout(handle)
  }, [])
  const anim = useFadeIn()
  if (loading) {
    return (
      <LoadingContainer style={anim}>
        <Loading />
      </LoadingContainer>
    )
  }
  return <Body {...props} />
}

export default Result
